import React, { useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useBlogIndex } from '../lib/useBlogIndex';
import ActivityHeatmap from '../components/ActivityHeatmap';
import { useDocumentTitle } from '../lib/useDocumentTitle';
import './Home.css';

const formatDate = (iso: string | null) =>
  iso
    ? new Date(iso).toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      })
    : '';

const Home: React.FC = () => {
  // `/` shows everything; `/category/:categorySlug` narrows to one section.
  const { categorySlug } = useParams<{ categorySlug?: string }>();
  const { posts, categories, loading, error } = useBlogIndex();

  const activeCategory = useMemo(
    () => categories.find((c) => c.slug === categorySlug) || null,
    [categories, categorySlug]
  );

  useDocumentTitle(activeCategory ? activeCategory.name : 'QED AI');

  const visiblePosts = useMemo(
    () => (categorySlug ? posts.filter((p) => p.category === categorySlug) : posts),
    [posts, categorySlug]
  );

  return (
    <div className="home-container">
      <header className="hero-section">
        <h1>
          {activeCategory ? (
            activeCategory.name
          ) : (
            <>
              Engineering the Future of
              <br />
              AI Infrastructure
            </>
          )}
        </h1>
        <p>
          {activeCategory
            ? activeCategory.description ||
              `Everything published under ${activeCategory.name}.`
            : 'Deep dives into architecting autonomous agents, scalable inference systems, and production-grade machine learning pipelines.'}
        </p>
      </header>

      {categories.length > 0 && (
        <nav className="category-nav" aria-label="Blog sections">
          <Link to="/" className={`category-chip${categorySlug ? '' : ' active'}`}>
            All
            <span className="chip-count">{posts.length}</span>
          </Link>
          {categories.map((category) => (
            <Link
              key={category.id}
              to={`/category/${category.slug}`}
              className={`category-chip${categorySlug === category.slug ? ' active' : ''}`}
            >
              {category.name}
              <span className="chip-count">{category.postCount ?? 0}</span>
            </Link>
          ))}
        </nav>
      )}

      <div className="section-label">
        <span>{activeCategory ? activeCategory.name : 'Latest Writing'}</span>
      </div>

      {error && (
        <div className="home-notice">
          Showing the archive — the live blog API is unreachable ({error})
        </div>
      )}

      {loading ? (
        <div className="home-loading">Loading articles…</div>
      ) : visiblePosts.length === 0 ? (
        <div className="home-empty">
          <h3>Nothing here yet</h3>
          <p>
            {categorySlug
              ? 'This section is waiting for its first article.'
              : 'The first article is on its way.'}
          </p>
        </div>
      ) : (
        <div className="blog-grid">
          {visiblePosts.map((post) => (
            <article key={post.id} className="blog-card">
              {post.coverImage && (
                <Link to={`/blog/${post.slug}`} className="card-cover">
                  <img src={post.coverImage} alt="" loading="lazy" />
                </Link>
              )}

              <div className="card-body">
                <div className="tags">
                  {post.tags.slice(0, 4).map((tag) => (
                    <span key={tag} className="tag">
                      {tag}
                    </span>
                  ))}
                </div>

                <h2 className="card-title">
                  <Link to={`/blog/${post.slug}`}>{post.title}</Link>
                </h2>
                <p className="card-excerpt">{post.excerpt}</p>

                <div className="card-footer">
                  <span className="author">{post.authorName}</span>
                  <span className="date">
                    {formatDate(post.publishedAt)} · {post.readingMinutes} min read
                  </span>
                </div>

                <br />

                <Link to={`/blog/${post.slug}`} className="read-more-link">
                  Read article
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path
                      d="M5 12H19M19 12L12 5M19 12L12 19"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </Link>
              </div>
            </article>
          ))}
        </div>
      )}

      {/* Below the posts: the writing is the point, the stats are the footnote.
          Only on the full index — a section view would show the same grid. */}
      {!categorySlug && posts.length > 0 && (
        <div className="home-heatmap">
          <ActivityHeatmap title="When we publish" />
        </div>
      )}
    </div>
  );
};

export default Home;
